import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../services/apiClient';

// The daily "Morning Brief" panel — a text rundown + a ~10-minute podcast, posted
// each morning (~5:30 AM) by the external AI layer. Used identically on the Exec
// dashboard (audience="exec") and on each PM's page (audience="pm", pmId set).
//
// Page weight: only TODAY's brief is loaded up front; history comes 5 days at a
// time on demand, and a day's audio player is only mounted once that day is
// expanded (preload="none") so opening the tab never fetches a stack of MP3s.
const NAVY = '#1e3a5f';
const ORANGE = '#e87722';
const PAGE = 5;

const fmtTime = s => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
};
const fmtDate = d => {
  if (!d) return '';
  const dt = new Date(String(d).length <= 10 ? d + 'T12:00:00' : d);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};
const todayStr = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local

function Player({ brief, onListened }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(brief.duration_seconds || 0);

  useEffect(() => { setPlaying(false); setCur(0); setDur(brief.duration_seconds || 0); }, [brief.id, brief.duration_seconds]);

  if (!brief.audio_url) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '12px 14px', color: '#9ca3af', fontSize: 13 }}>
        <span style={{ fontSize: 18 }}>🎙️</span> Audio hasn’t been posted for this day yet.
      </div>
    );
  }
  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };
  const seek = e => {
    const a = audioRef.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * dur;
    setCur(a.currentTime);
  };
  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '12px 14px' }}>
      {/* preload="none" — the file is only fetched when the user actually plays it. */}
      <audio ref={audioRef} src={brief.audio_url} preload="none"
        onLoadedMetadata={e => setDur(e.target.duration || brief.duration_seconds || 0)}
        // Most of the way through counts — nobody sits through the sign-off.
        onTimeUpdate={e => { setCur(e.target.currentTime); const d = e.target.duration; if (onListened && d && e.target.currentTime / d > 0.9) onListened(); }}
        onEnded={() => { setPlaying(false); onListened && onListened(); }} />
      <button onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}
        style={{ flexShrink: 0, width: 50, height: 50, borderRadius: '50%', border: 'none', background: NAVY, color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>
        {playing ? '⏸' : '▶'}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{brief.title || 'Titan Morning Brief'}</span>
          <span style={{ fontSize: 10, fontWeight: 800, color: '#9a3412', background: '#ffedd5', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>🎙️ podcast</span>
        </div>
        <div onClick={seek} style={{ height: 6, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden', cursor: 'pointer' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: ORANGE, borderRadius: 999 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginTop: 5 }}>
          <span>{fmtTime(cur)}</span><span>{fmtTime(dur)}</span>
        </div>
      </div>
      <a href={brief.audio_url} target="_blank" rel="noreferrer" title="Open / download audio" style={{ flexShrink: 0, fontSize: 16, color: '#6b7280', textDecoration: 'none' }}>⬇</a>
    </div>
  );
}

// Full card for a brief: player + the written rundown.
function BriefCard({ brief, isToday, onDone }) {
  const paras = String(brief.summary || '').split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
  // Reading the rundown to the end counts as done, the same as hearing the podcast out.
  const endRef = useRef(null);
  useEffect(() => {
    if (!onDone || !endRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    // Only once the reader has actually moved: a short rundown has its end on screen the
    // moment the page opens, and that must not count as read.
    let scrolled = false;
    const onScroll = () => { scrolled = true; };
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    const io = new IntersectionObserver(es => { if (scrolled && es.some(e => e.isIntersecting)) { onDone('read'); io.disconnect(); } }, { threshold: 0.5 });
    io.observe(endRef.current);
    return () => { io.disconnect(); window.removeEventListener('scroll', onScroll, { capture: true }); };
  }, [brief.id, onDone]);
  return (
    <div style={{ background: '#fff', border: '1px solid #eef2f7', borderTop: `3px solid ${ORANGE}`, borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>{fmtDate(brief.brief_date)}</span>
        {isToday && <span style={{ fontSize: 10, fontWeight: 800, color: '#166534', background: '#dcfce7', borderRadius: 999, padding: '2px 9px', textTransform: 'uppercase', letterSpacing: 0.4 }}>Today</span>}
        {onDone && (
          <button onClick={() => onDone('folded')} title="Fold the brief away — it comes back tomorrow, or with the Open button"
            style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: NAVY, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '5px 12px', cursor: 'pointer' }}>
            ✓ Done — fold it away
          </button>
        )}
      </div>
      <Player brief={brief} onListened={onDone ? () => onDone('listened') : undefined} />
      <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 8px' }}>The rundown</div>
      {paras.length ? (
        <div style={{ fontSize: 13.5, lineHeight: 1.7, color: '#374151' }}>
          {paras.map((p, i) => <p key={i} style={{ margin: '0 0 10px' }}>{p}</p>)}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No written summary for this day.</div>
      )}
      <div ref={endRef} style={{ height: 1 }} />
    </div>
  );
}

// A collapsed history row — expands in place into a full card (audio mounts only then).
function HistoryRow({ brief }) {
  const [open, setOpen] = useState(false);
  const teaser = String(brief.summary || '').replace(/\s+/g, ' ').slice(0, 90);
  if (open) {
    return (
      <div>
        <BriefCard brief={brief} isToday={brief.brief_date === todayStr()} />
        <button onClick={() => setOpen(false)}
          style={{ marginTop: 6, background: 'none', border: 'none', color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '4px 2px' }}>▲ Collapse</button>
      </div>
    );
  }
  return (
    <div onClick={() => setOpen(true)}
      style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #eef2f7', borderRadius: 10, padding: '11px 14px', cursor: 'pointer' }}>
      <span style={{ flexShrink: 0, width: 34, height: 34, borderRadius: '50%', background: '#eef2f7', color: NAVY, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▶</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{fmtDate(brief.brief_date)}</div>
        {teaser && <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{teaser}{brief.summary && brief.summary.length > 90 ? '…' : ''}</div>}
      </div>
      <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{brief.duration_seconds ? fmtTime(brief.duration_seconds) : ''}</span>
      <span style={{ color: '#d1d5db', flexShrink: 0 }}>›</span>
    </div>
  );
}

// A brief that's been read, heard or folded stays folded on THIS device, for THIS
// brief — tomorrow's arrives open. A one-line strip stays so it can be unfolded.
const foldKey = brief => `te_brief_done:${brief.id}`;
const readDone = brief => { try { return localStorage.getItem(foldKey(brief)) || ''; } catch { return ''; } };
const writeDone = (brief, how) => { try { localStorage.setItem(foldKey(brief), how); } catch { /* private mode etc. */ } };

export default function MorningBriefs({ audience = 'exec', pmId = null, pmName = '' }) {
  const [latest, setLatest] = useState(null);        // today's (or most recent) brief
  const [folded, setFolded] = useState('');          // '' | 'read' | 'listened' | 'folded'
  const [forceOpen, setForceOpen] = useState(false); // unfolded by hand — stays open until the page is left
  const [history, setHistory] = useState([]);        // older briefs, loaded 5 at a time
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMore, setNoMore] = useState(false);
  const [err, setErr] = useState('');

  const qs = extra => (audience === 'pm'
    ? `?audience=pm&pm_id=${encodeURIComponent(pmId)}&${extra}`
    : `?audience=exec&${extra}`);

  useEffect(() => {
    setLoading(true); setErr(''); setLatest(null); setHistory([]); setShowHistory(false); setNoMore(false);
    apiFetch(`/morning-briefs${qs('limit=1')}`)
      .then(d => setLatest(Array.isArray(d) && d.length ? d[0] : null))
      .catch(e => setErr(e?.error || 'Could not load morning briefs.'))
      .finally(() => setLoading(false));
  }, [audience, pmId]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadMore() {
    setLoadingMore(true);
    const offset = 1 + history.length;             // skip the one already shown
    apiFetch(`/morning-briefs${qs(`limit=${PAGE}&offset=${offset}`)}`)
      .then(d => {
        const rows = Array.isArray(d) ? d : [];
        setHistory(prev => [...prev, ...rows]);
        if (rows.length < PAGE) setNoMore(true);
        setShowHistory(true);
      })
      .catch(() => setNoMore(true))
      .finally(() => setLoadingMore(false));
  }

  if (err) return <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', color: '#dc2626', fontSize: 14 }}>{err}</div>;
  if (loading) return <div style={{ color: 'var(--text-muted)', padding: '30px 0' }}>Loading morning briefs…</div>;

  const who = pmName ? pmName.split(' ')[0] + '’s' : '';
  if (!latest) {
    return (
      <div style={{ background: '#fff', border: '1px dashed #e5e7eb', borderRadius: 12, padding: '28px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
        <div style={{ fontSize: 26, marginBottom: 8 }}>🎙️</div>
        No {who ? who + ' ' : ''}morning briefs yet. The AI brief posts each morning around 5:30 AM.
      </div>
    );
  }

  const isToday = latest.brief_date === todayStr();
  const doneHow = folded || readDone(latest);
  // Folding on read happens only after a pause — flying past the end while scrolling
  // to something else shouldn't count. Listening and the button are immediate.
  const markDone = how => {
    // Reopened by hand: only the button folds it again — not scrolling, not the audio.
    if (forceOpen) { if (how === 'folded') setForceOpen(false); return; }
    if (readDone(latest)) return;
    if (how === 'read') { setTimeout(() => { if (!readDone(latest)) { writeDone(latest, 'read'); setFolded('read'); } }, 1500); return; }
    writeDone(latest, how); setFolded(how);
  };
  const unfold = () => { setForceOpen(true); };
  const btn = { fontSize: 12.5, fontWeight: 700, color: NAVY, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '7px 16px', cursor: 'pointer' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
        <span style={{ display: 'inline-flex', width: 7, height: 7, borderRadius: '50%', background: '#16a34a' }} />
        Auto-posted every morning around 5:30 AM by the AI brief
        {!isToday && <span style={{ color: '#b45309' }}>· today’s hasn’t posted yet — showing the most recent</span>}
      </div>

      {doneHow && !forceOpen ? (
        <button onClick={unfold}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #eef2f7', borderLeft: `4px solid ${ORANGE}`, borderRadius: 10, padding: '11px 16px', cursor: 'pointer', font: 'inherit' }}>
          <span style={{ fontSize: 16 }}>🎙️</span>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: '#111' }}>{fmtDate(latest.brief_date)}{isToday ? ' — today’s brief' : ''}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{doneHow === 'listened' ? 'listened' : doneHow === 'read' ? 'read' : 'folded away'} ✓</span>
          <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, color: NAVY }}>Open it ›</span>
        </button>
      ) : (
        <BriefCard brief={latest} isToday={isToday} onDone={markDone} />
      )}

      {/* History — hidden until asked for, then 5 days at a time. */}
      {showHistory && history.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 10px' }}>Earlier briefs</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map(b => <HistoryRow key={b.id} brief={b} />)}
          </div>
        </>
      )}

      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
        {loadingMore ? (
          <span style={{ fontSize: 12.5, color: '#9ca3af' }}>Loading…</span>
        ) : !showHistory ? (
          <button onClick={loadMore} style={btn}>Show last {PAGE} days</button>
        ) : !noMore ? (
          <button onClick={loadMore} style={btn}>Show more</button>
        ) : (
          <span style={{ fontSize: 12, color: '#9ca3af' }}>That’s all the briefs.</span>
        )}
      </div>
    </div>
  );
}
